<div class="p-6">
  @if (loading) {
    <div class="flex justify-center py-12"><div class="animate-spin rounded-full h-7 w-7 border-b-2 border-brand-500"></div></div>
  }

  @if (error) {
    <div class="mb-4 p-3 rounded-lg bg-error-50 border border-error-200 text-sm text-error-700 dark:bg-error-500/10 dark:border-error-500/20 dark:text-error-400">
      {{ error }}
    </div>
  }

  @if (!loading) {
    <form [formGroup]="form" (ngSubmit)="guardar()" class="space-y-6">

      <!-- Identificación -->
      <section class="space-y-4">
        <h3 class="text-xs font-semibold uppercase text-gray-500 tracking-wider">Identificación</h3>

        <div>
          <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Etiqueta visible <span class="text-error-500">*</span>
          </label>
          <input type="text" formControlName="label"
            (input)="onLabelInput($any($event.target).value)"
            placeholder="Ej. Urgente"
            class="dark:bg-dark-900 h-11 w-full rounded-lg border bg-transparent px-4 py-2.5 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
            [class.border-error-500]="hasError('label')" [class.border-gray-300]="!hasError('label')"/>
          @if (hasError('label')) { <p class="mt-1.5 text-sm text-error-500">{{ getError('label') }}</p> }
        </div>

        <div>
          <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Código interno <span class="text-error-500">*</span>
          </label>
          <input type="text" formControlName="name"
            (input)="onNameInput()"
            placeholder="URGENTE"
            class="dark:bg-dark-900 h-11 w-full rounded-lg border bg-transparent px-4 py-2.5 text-sm uppercase font-mono dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
            [class.border-error-500]="hasError('name')" [class.border-gray-300]="!hasError('name')"/>
          <p class="mt-1 text-xs text-gray-500">Se completa automáticamente desde la etiqueta.</p>
          @if (hasError('name')) { <p class="mt-1.5 text-sm text-error-500">{{ getError('name') }}</p> }
        </div>
      </section>

      <!-- Nivel (selector visual) -->
      <section class="space-y-3">
        <h3 class="text-xs font-semibold uppercase text-gray-500 tracking-wider">Nivel de urgencia <span class="text-error-500">*</span></h3>

        <div class="grid grid-cols-2 md:grid-cols-4 gap-2">
          @for (opt of LEVEL_OPTIONS; track opt.value) {
            <button type="button" (click)="setLevel(opt.value)"
              class="p-3 rounded-lg border-2 text-left transition-all"
              [class.border-brand-500]="form.value.level === opt.value"
              [class.bg-brand-50]="form.value.level === opt.value"
              [class.dark:bg-brand-900/20]="form.value.level === opt.value"
              [class.border-gray-200]="form.value.level !== opt.value"
              [class.dark:border-gray-700]="form.value.level !== opt.value"
              [class.hover:border-gray-300]="form.value.level !== opt.value">
              <div class="flex items-center gap-2 mb-1">
                <span class="w-3 h-3 rounded-full" [style.background-color]="opt.color"></span>
                <span class="font-mono text-xs text-gray-500">Nivel {{ opt.value }}</span>
              </div>
              <div class="font-semibold text-sm text-gray-800 dark:text-gray-100">{{ opt.label }}</div>
              <div class="text-xs text-gray-500 mt-0.5">{{ opt.description }}</div>
            </button>
          }
        </div>
        <p class="text-xs text-gray-500">1 = más urgente · 4 = menos urgente. Determina el orden por defecto.</p>
      </section>

      <!-- Color -->
      <section class="space-y-3">
        <h3 class="text-xs font-semibold uppercase text-gray-500 tracking-wider">Color del badge</h3>

        <div class="flex flex-wrap gap-2">
          @for (c of COLOR_PRESETS; track c) {
            <button type="button" (click)="setColor(c)"
              class="w-8 h-8 rounded-lg border-2 transition-all hover:scale-110"
              [style.background-color]="c"
              [class.border-gray-900]="form.value.color === c"
              [class.dark:border-white]="form.value.color === c"
              [class.border-transparent]="form.value.color !== c"
              [title]="c">
            </button>
          }
        </div>

        <div class="flex items-center gap-3">
          <div class="relative">
            <input type="color" formControlName="color"
              class="absolute inset-0 w-12 h-12 opacity-0 cursor-pointer"/>
            <div class="w-12 h-12 rounded-lg border-2 border-gray-200 dark:border-gray-700 shadow-sm pointer-events-none"
              [style.background-color]="form.value.color || '#F44336'"></div>
          </div>
          <input type="text" formControlName="color" placeholder="#F44336"
            class="flex-1 h-11 rounded-lg border border-gray-300 bg-transparent px-3 text-sm font-mono uppercase dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"/>
        </div>
      </section>

      <!-- Activo -->
      <section>
        <label class="flex items-start gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-800/40 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800/60 transition-colors">
          <div class="relative inline-flex shrink-0 mt-0.5">
            <input type="checkbox" formControlName="isActive" class="peer sr-only"/>
            <div class="w-11 h-6 bg-gray-300 dark:bg-gray-700 rounded-full peer-checked:bg-brand-500 transition-colors"></div>
            <div class="absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform peer-checked:translate-x-5"></div>
          </div>
          <div class="flex-1">
            <span class="block text-sm font-medium text-gray-800 dark:text-gray-100">Prioridad activa</span>
            <span class="block text-xs text-gray-500">Si se desactiva, no aparecerá en los selectores al crear tareas.</span>
          </div>
        </label>
      </section>

      <div class="flex items-center justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
        <app-button variant="outline" type="button" (btnClick)="cancelar()">Cancelar</app-button>
        <app-button variant="primary" type="submit" [disabled]="saving || form.invalid">
          {{ saving ? 'Guardando...' : (isEditing ? 'Actualizar' : 'Crear') }}
        </app-button>
      </div>
    </form>
  }
</div>
